import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AdaptiveButton from '../../components/AdaptiveButton';
import Button from '../../components/Button';
import ButtonBar from '../../components/ButtonBar';
import PageHeader from '../../components/PageHeader';
import { AMLSummaryWithSkeleton } from '../../components/Skeletons/ItemsWithSkeleton';
import ListItemSkeleton from '../../components/Skeletons/ListItemSkeleton';
import AppConfig from '../../config';
import { t } from '../../lang';
import RequestService from '../../services/RequestService';
import { appSlice } from '../../store/slices';
import './AMLHistoryPage.scss';

const COUNT_LOAD = 10;
const MIN_SKELETON_ITEM_LOAD_TIME = 1000;

const AMLHistoryPage = () => {
	const dispatch = useDispatch();
	const { routes } = AppConfig;
	const navigate = useNavigate();

	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [stop, setStop] = useState(false);

	const observerRef = useRef(null);
	const pageRef = useRef(1);
	const isFetchingRef = useRef(false);

	const isInitialLoading = loading && items.length === 0;
	const isLoadingMore = loading && items.length > 0;

	const getItemDateValue = item => item.Created;

	const safeParseDate = value => {
		if (!value || typeof value !== 'string') return null;
		const d = new Date(value);
		return Number.isNaN(d.getTime()) ? null : d;
	};

	const dateKeyUTC = d => {
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	};

	const fetchPage = useCallback(async pageToLoad => {
		try {
			const res = await RequestService.AmlHistory({ Page: pageToLoad, Count: COUNT_LOAD });

			if (res.Status === 'Error') {
				console.error('AML API error', res);
				return [];
			}

			return Array.isArray(res?.AddressesAml) ? res.AddressesAml : [];
		} catch (e) {
			console.error('AML HISTORY ERROR', e);
			return [];
		}
	}, []);

	const sleep = ms => new Promise(r => setTimeout(r, ms));

	const fetchNext = useCallback(async () => {
		if (isFetchingRef.current || stop) return;

		isFetchingRef.current = true;
		setLoading(true);

		const startTime = Date.now();

		try {
			const pageToLoad = pageRef.current;
			const batch = await fetchPage(pageToLoad);

			if (!batch.length) {
				setStop(true);
				return;
			}

			setItems(prev => {
				const map = new Map(prev.map(x => [x.Id, x]));
				for (const it of batch) map.set(it.Id, it);
				return Array.from(map.values());
			});

			pageRef.current += 1;
		} finally {
			const elapsed = Date.now() - startTime;
			const remaining = MIN_SKELETON_ITEM_LOAD_TIME - elapsed;

			if (remaining > 0) await sleep(remaining);

			setLoading(false);
			isFetchingRef.current = false;
		}
	}, [fetchPage, stop]);

	useEffect(() => {
		fetchNext();
	}, []);

	const grouped = useMemo(() => {
		const map = new Map();
		for (const it of items) {
			const d = safeParseDate(getItemDateValue(it));
			if (!d) continue;

			const key = dateKeyUTC(d);
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(it);
		}
		return map;
	}, [items]);

	const sectionTitle = dateKey => {
		const [y, m, d] = dateKey.split('-').map(Number);
		const target = new Date(Date.UTC(y, m - 1, d));
		const now = new Date();
		const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
		const targetUTC = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
		const diffDays = Math.floor((todayUTC - targetUTC) / (1000 * 60 * 60 * 24));

		if (diffDays === 0) return t('Today_uppercase');
		if (diffDays === 1) return t('Yesterday_uppercase');

		const month = target.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
		return `${month} ${target.getUTCDate()} / ${target.getUTCFullYear()}`;
	};

	useEffect(() => {
		const el = observerRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !loading && !stop) {
					fetchNext();
				}
			},
			{ root: null, rootMargin: '0px', threshold: 0.1 }
		);
		observer.observe(el);

		return () => observer.disconnect();
	}, [fetchNext, loading, stop]);

	const onAmlScanClick = () => {
		navigate({ pathname: routes.amlCheck });
	};

	const onLearNMoreClick = () => {
		dispatch(
			appSlice.actions.addModal({
				isHeaderWithColorBgAndIcon: true,
				title: t('AML Risk'),
				subtitle: t('How AML risk is calculated'),
				headerBackgroundColor: '#0b8d57',
				headerIcon: 'DefaultAML',
				tag: 'LearnAboutAmlRiskModal',
				isOpen: true,
				height: '65%',
			})
		);
	};

	return (
		<div className='AMLHistoryPage'>
			<PageHeader header={t('AML check history')} />

			<div className='AMLHistoryPage_container'>
				{isInitialLoading &&
					[8].map((count, index) => (
						<div className='FormBlock' key={index}>
							{[...Array(count)].map((_, index) => (
								<ListItemSkeleton key={index} />
							))}
						</div>
					))}

				{!isInitialLoading &&
					Array.from(grouped.entries()).map(([key, groupItems]) => (
						<div key={key} className='AMLHistoryPage_container_block'>
							<div className='AMLHistoryPage_container_block_date'>{sectionTitle(key)}</div>
							<div className='AMLHistoryPage_container_block_aml'>
								<AMLSummaryWithSkeleton loading={false} items={groupItems} passIndex />
							</div>
						</div>
					))}

				{isLoadingMore && (
					<div className='FormBlock'>
						<ListItemSkeleton />
					</div>
				)}

				{items.length > 0 && !stop && <div ref={observerRef} style={{ height: '20px' }} />}
			</div>

			<ButtonBar>
				<div className='AMLHistoryPage_buttonBar'>
					<AdaptiveButton onClick={onLearNMoreClick} icon='QUESTIONMARK' />
					<Button iconBefore={'AML_SCAN'} styleType='green-dark' onClick={onAmlScanClick}>
						{t('AML check')}
					</Button>
				</div>
			</ButtonBar>
		</div>
	);
};

export default AMLHistoryPage;
